<template>
  <v-container id="technical-review-detail" class="position-relative">
    <app-city-header />

    <v-overlay :value="actionLoading" absolute>
      <v-progress-circular indeterminate color="primary" size="64" />
    </v-overlay>

    <v-row class="mt-4" v-if="revisao">
      <v-col cols="12" md="8">
        <v-card outlined>
          <v-card-title class="d-flex align-center justify-space-between">
            <div>
              <div class="headline">{{ revisao.perguntaTitulo }}</div>
              <div class="grey--text">{{ revisao.grupoNome }}</div>
            </div>
            <technical-review-status-chip :status="revisao.status" />
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <strong>Aberto em</strong>
                <div>{{ formatDate(revisao.dataCriacao) }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <strong>Última atualização</strong>
                <div>{{ formatDate(revisao.dataAtualizacao || revisao.dataCriacao) }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <strong>Solicitante</strong>
                <div>{{ revisao.responsavelNome || '—' }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-4">
          <v-card-title>Justificativa</v-card-title>
          <v-card-text>
            <div v-if="justificativa" class="text-preline">
              {{ justificativa }}
            </div>
            <div v-else class="grey--text">Nenhuma justificativa registrada.</div>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-4">
          <v-card-title>Anexos</v-card-title>
          <v-card-text>
            <v-list dense v-if="anexos.length">
              <v-list-item v-for="anexo in anexos" :key="anexo.id">
                <v-list-item-content>
                  <v-list-item-title>{{ anexo.nome }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Enviado em {{ formatDate(anexo.dataEnvio) }} por
                    {{ anexo.usuarioNome || '—' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click="download(anexo)">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <div v-else class="grey--text">Nenhum anexo disponível.</div>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-4">
          <v-card-title>Interações</v-card-title>
          <v-card-text>
            <v-timeline dense v-if="mensagens.length">
              <v-timeline-item
                v-for="mensagem in mensagens"
                :key="mensagem.id"
                :color="timelineColor(mensagem)"
                small
              >
                <strong>{{ mensagem.usuarioNome || 'Sistema' }}</strong>
                <span class="grey--text"> — {{ formatDateTime(mensagem.dataCriacao) }}</span>
                <div v-if="mensagem.status">
                  Status: {{ statusText(mensagem.status) }}
                </div>
                <div>{{ mensagem.mensagem || '—' }}</div>
              </v-timeline-item>
            </v-timeline>
            <div v-else class="grey--text">Nenhuma interação registrada.</div>
          </v-card-text>
        </v-card>

        <v-card
          outlined
          class="mt-4"
          v-if="canManage && isEditable && !hasAnalysisPermission"
        >
          <v-card-title>Adicionar comentário</v-card-title>
          <v-card-text>
            <v-textarea
              v-model="novoComentario"
              label="Mensagem"
              auto-grow
              clearable
            />
            <v-btn
              color="primary"
              class="mt-2"
              @click="enviarComentario"
              :loading="actionLoading"
              :disabled="!novoComentario || actionLoading"
            >
              Registrar comentário
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card
          outlined
          class="mb-4"
          v-if="canManage && isEditable && !hasAnalyserFlag"
        >
          <v-card-title>Ações rápidas</v-card-title>
          <v-card-text>
            <v-btn
              block
              color="primary"
              class="mb-2"
              @click="enviarParaAnalise"
              v-if="podeEnviar"
              :loading="actionLoading"
              :disabled="actionLoading"
            >
              Enviar para análise
            </v-btn>
            <v-btn
              block
              color="error"
              outlined
              v-if="canCancel"
              @click="cancelarRevisaoTecnica"
              :loading="actionLoading"
              :disabled="actionLoading"
            >
              Cancelar revisão técnica
            </v-btn>
            <v-btn
              block
              color="secondary"
              class="mt-2"
              outlined
              v-if="podeEditarRascunho && revisao && revisao.id"
              :to="editRoute"
            >
              Editar rascunho
            </v-btn>
          </v-card-text>
        </v-card>

        <v-card outlined v-if="showAnalysisCard">
          <v-card-title>Gestão da revisão técnica</v-card-title>
          <v-card-text>
            <v-alert
              v-if="analysisLocked"
              type="info"
              dense
              outlined
              class="mb-3"
            >
              Esta revisão técnica foi concluída e não pode mais ser editada ou
              reavaliada.
            </v-alert>
            <v-select
              v-model="avaliacao.status"
              :items="statusAnaliseOptions"
              label="Alterar status"
              :disabled="!canAnalyse"
            />
            <v-select
              v-if="shouldShowFaixaSelect"
              v-model="avaliacao.faixaRespostaId"
              :items="faixaOptions"
              :loading="faixaLoading"
              :disabled="!canAnalyse"
              item-text="text"
              item-value="value"
              label="Faixa de pontuação homologada"
              hint="Selecione a pontuação homologada para este critério"
              persistent-hint
              :no-data-text="faixaLoading ? 'Carregando...' : 'Nenhuma faixa disponível'"
            />
            <v-textarea
              v-model="avaliacao.parecer"
              label="Parecer (opcional)"
              auto-grow
              :disabled="!canAnalyse"
            />
            <v-textarea
              v-model="avaliacao.mensagem"
              :label="analysisMessageLabel"
              auto-grow
              hint="Essa mensagem aparecerá na linha do tempo"
              persistent-hint
              :disabled="!canAnalyse"
            />
            <v-file-input
              v-model="avaliacao.arquivos"
              label="Anexos (opcional)"
              multiple
              show-size
              truncate-length="25"
              hint="Arquivos enviados ficarão visíveis ao solicitante"
              persistent-hint
              :disabled="!canAnalyse"
            />
            <v-btn
              color="success"
              block
              class="mt-3"
              @click="aplicarStatus"
              :loading="actionLoading"
              :disabled="!avaliacao.status || !canAnalyse || actionLoading"
            >
              Aplicar
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12" class="text-center grey--text">
        Carregando revisão técnica...
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { AppCityHeader, cityAPI } from "@/modules/city";
import { technicalReviewAPI } from "../API/TechnicalReviewAPI";
import { cityFaixaAPI } from "@/modules/city/API/CityFaixaAPI";
import { shouldTreatAsNational } from "@/utils/nationalEvaluation";
import {
  REVIEW_STATUS,
  REVIEW_STATUSES,
  findStatusMeta,
  isReviewStatusGestaoNacional,
  normalizeReviewStatus,
} from "../constants";
import { extractCriterionDescription } from "@/helpers/criteria";
import TechnicalReviewStatusChip from "./StatusChip.vue";

export default {
  name: "TechnicalReviewDetail",
  components: { AppCityHeader, TechnicalReviewStatusChip },
  data() {
      return {
        loading: false,
        revisao: null,
        justificativa: "",
        anexos: [],
        mensagens: [],
        novoComentario: "",
        avaliacao: {
          status: null,
          mensagem: "",
          parecer: "",
          arquivos: [],
          faixaRespostaId: null,
        },
      config: JSON.parse(localStorage.getItem("config")) || {},
      groupsConfig: JSON.parse(localStorage.getItem("groupsConfig")) || {},
      actionLoading: false,
      faixaOptions: [],
      faixaLoading: false,
      faixaLoadedForId: null,
    };
  },
  computed: {
    ...mapGetters(["isFullAdmin", "cityId", "city"]),
    municipioId() {
      return this.$route.params.municipioId || this.cityId;
    },
    canManage() {
      return (
        this.isFullAdmin ||
        ((this.config.revisaoTecnica ?? false) &&
          (this.groupsConfig.revisaoTecnica ?? false))
      );
    },
    hasAnalyserFlag() {
      const configAllowed = this.normalizeBooleanFlag(
        this.config.analisarRevisaoTecnica,
        false
      );
      const groupAllowed = this.normalizeBooleanFlag(
        this.groupsConfig.analisarRevisaoTecnica,
        false
      );

      return configAllowed && groupAllowed;
    },
    hasNationalEvaluationFlag() {
      const configAllowed = this.normalizeBooleanFlag(
        this.config.avaliaRevisaoTecnicaNacional,
        true
      );
      const groupAllowed = this.normalizeBooleanFlag(
        this.groupsConfig.avaliaRevisaoTecnicaNacional,
        true
      );

      return configAllowed && groupAllowed;
    },
    hasStateEvaluationFlag() {
      const configAllowed = this.normalizeBooleanFlag(
        this.config.avaliaRevisaoTecnicaEstadual,
        true
      );
      const groupAllowed = this.normalizeBooleanFlag(
        this.groupsConfig.avaliaRevisaoTecnicaEstadual,
        true
      );

      return configAllowed && groupAllowed;
    },
    isNationalReview() {
      if (!this.revisao) {
        return false;
      }

      const ehClienteOculto = this.normalizeBooleanFlag(
        this.revisao.ehPerguntaClienteOculto,
        false
      );

      return shouldTreatAsNational(
        this.revisao.perguntaId,
        ehClienteOculto
      );
    },
    isFinalStatus() {
      return (
        this.revisao &&
        ["DEFERIDO", "INDEFERIDO"].includes(this.revisao.status)
      );
    },
    hasAnalysisPermission() {
      if (this.isFullAdmin) {
        return true;
      }

      if (!this.hasAnalyserFlag) {
        return false;
      }

      if (!this.revisao) {
        return true;
      }

      if (this.isFinalStatus) {
        return true;
      }

      return this.isNationalReview
        ? this.hasNationalEvaluationFlag
        : this.hasStateEvaluationFlag;
    },
    analysisLocked() {
      return this.isFinalStatus;
    },
    canAnalyse() {
      return this.hasAnalysisPermission && !this.analysisLocked;
    },
    showAnalysisCard() {
      return this.hasAnalysisPermission;
    },
    requiresFaixaSelection() {
      return (
        this.revisao &&
        !this.revisao.ehPerguntaClienteOculto &&
        Boolean(this.revisao.perguntaFaixaId)
      );
    },
    shouldShowFaixaSelect() {
      return this.requiresFaixaSelection && this.avaliacao.status === "DEFERIDO";
    },
    analysisMessageLabel() {
      return this.hasAnalysisPermission
        ? "Mensagem ao Solicitante"
        : "Mensagem ao município";
    },
    canCancel() {
      if (!this.canManage || !this.isEditable) {
        return false;
      }

      if (this.isFullAdmin) {
        return true;
      }

      const configFlag = this.config.cancelarRevisaoTecnica;
      const groupFlag = this.groupsConfig.cancelarRevisaoTecnica;

      const configAllowed = configFlag === undefined ? true : Boolean(configFlag);
      const groupAllowed = groupFlag === undefined ? true : Boolean(groupFlag);

      return configAllowed && groupAllowed;
    },
    statusMeta() {
      if (!this.revisao) {
        return null;
      }

      const status = this.revisao.status
        ? this.revisao.status
        : normalizeReviewStatus(this.revisao.rawStatus);

      return findStatusMeta(status);
    },
    isEditable() {
      return (
        this.revisao &&
        ["RASCUNHO", "COMPLEMENTAR", "EM_ANALISE", "ENVIADO"].includes(
          this.revisao.status
        )
      );
    },
    podeEditarRascunho() {
      return (
        this.revisao &&
        ["RASCUNHO", "COMPLEMENTAR"].includes(this.revisao.status)
      );
    },
    podeEnviar() {
      return (
        this.revisao &&
        ["RASCUNHO", "COMPLEMENTAR"].includes(this.revisao.status)
      );
    },
    editRoute() {
      if (!this.revisao || !this.revisao.id) {
        return "";
      }
      return `/cidade/${this.municipioId}/revisoes-tecnicas/${this.revisao.id}/editar`;
    },
    statusAnaliseOptions() {
      const allowedStatuses = [
        REVIEW_STATUS.EM_ANALISE,
        REVIEW_STATUS.GESTAO_NACIONAL,
        REVIEW_STATUS.DEFERIDO,
        REVIEW_STATUS.INDEFERIDO,
      ];

      return REVIEW_STATUSES.filter((status) => {
        if (!allowedStatuses.includes(status.value)) {
          return false;
        }

        if (
          status.value === REVIEW_STATUS.GESTAO_NACIONAL &&
          !this.hasNationalEvaluationFlag
        ) {
          return false;
        }

        return true;
      });
    },
  },
  watch: {
    "$route.params.municipioId"(novoMunicipioId, municipioIdAnterior) {
      if (novoMunicipioId !== municipioIdAnterior) {
        this.syncCityContext();
      }
    },
    "$route.params.revisaoId"() {
      this.carregar();
    },
    shouldShowFaixaSelect(newValue) {
      if (newValue) {
        this.carregarFaixaOpcoes();
      } else {
        this.avaliacao.faixaRespostaId = null;
      }
    },
    "revisao.perguntaFaixaId"(novoValor, antigoValor) {
      if (novoValor !== antigoValor) {
        this.faixaOptions = [];
        this.faixaLoadedForId = null;
        if (this.shouldShowFaixaSelect) {
          this.carregarFaixaOpcoes();
        }
      }
    },
    "avaliacao.status"(novoStatus) {
      if (novoStatus !== "DEFERIDO") {
        this.avaliacao.faixaRespostaId = null;
      } else if (this.shouldShowFaixaSelect) {
        this.carregarFaixaOpcoes();
      }
    },
  },
  created() {
    this.syncCityContext();
    this.carregar();
  },
  methods: {
    ...mapActions(["setCityId", "setCityName"]),
    normalizeBooleanFlag(value, defaultValue = false) {
      if (value === undefined || value === null) {
        return defaultValue;
      }

      if (value === true || value === 1) {
        return true;
      }

      if (value === false || value === 0) {
        return false;
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "sim", "yes"].includes(normalized)) {
          return true;
        }
        if (["0", "false", "nao", "não", "no"].includes(normalized)) {
          return false;
        }
      }

      return Boolean(value);
    },
    notifySuccess(message) {
      if (this.$toast?.success) {
        this.$toast.success(message);
      } else if (this.$notification?.success) {
        this.$notification.success(message);
      } else {
        console.info(message);
      }
    },
    notifyError(message) {
      if (this.$toast?.error) {
        this.$toast.error(message);
      } else if (this.$notification?.error) {
        this.$notification.error(message);
      } else {
        console.error(message);
      }
    },
    async syncCityContext(cabecalho = null) {
      const routeMunicipioId = this.$route?.params?.municipioId;
      const cabecalhoMunicipioId =
        cabecalho?.MunicipioId ?? cabecalho?.municipioId ?? null;

      const finalMunicipioId =
        routeMunicipioId ?? cabecalhoMunicipioId ?? null;

      if (finalMunicipioId === null || finalMunicipioId === undefined) {
        return;
      }

      const normalizedMunicipioId = Number(finalMunicipioId);

      if (Number.isNaN(normalizedMunicipioId)) {
        return;
      }

      const idComoTexto = String(normalizedMunicipioId);
      const idAtualNaStore = this.cityId ? String(this.cityId) : null;

      if (idAtualNaStore !== idComoTexto) {
        this.setCityId(idComoTexto);
      }

      const precisaAtualizarNome =
        idAtualNaStore !== idComoTexto || !this.city;

      const nomeFallback = this.resolverNomeMunicipio(cabecalho);

      if (precisaAtualizarNome) {
        await this.carregarNomeMunicipio(normalizedMunicipioId, nomeFallback);
      }
    },
    async carregarNomeMunicipio(municipioId, nomeFallback = null) {
      try {
        const { data } = await cityAPI.get(municipioId);
        const nomeMunicipio =
          data?.nome ??
          data?.Nome ??
          data?.municipioNome ??
          data?.MunicipioNome ??
          data?.municipio ??
          data?.Municipio ??
          nomeFallback;

        if (nomeMunicipio) {
          this.setCityName(nomeMunicipio);
        }
      } catch (error) {
        if (nomeFallback) {
          this.setCityName(nomeFallback);
        }
        console.error(
          "Não foi possível carregar o nome do município para a revisão técnica",
          error
        );
      }
    },
    resolverNomeMunicipio(cabecalho) {
      if (!cabecalho) {
        return null;
      }

      return (
        cabecalho.MunicipioNome ??
        cabecalho.municipioNome ??
        cabecalho.Municipio ??
        cabecalho.municipio ??
        cabecalho.SalaParceiroMunicipioNome ??
        cabecalho.salaParceiroMunicipioNome ??
        cabecalho.SalaParceiroNome ??
        cabecalho.salaParceiroNome ??
        null
      );
    },
    async carregar() {
      this.loading = true;
      try {
        const { data } = await technicalReviewAPI.get(
          this.$route.params.revisaoId
        );
        const cabecalho = data.Cabecalho || data.cabecalho;
        if (!cabecalho) {
          throw new Error("Dados da revisão técnica inválidos");
        }

        await this.syncCityContext(cabecalho);

        const ehClienteOculto = this.normalizeBooleanFlag(
          cabecalho.EhPerguntaClienteOculto ??
            cabecalho.ehPerguntaClienteOculto ??
            false,
          false
        );

        const perguntaMunicipioIdRaw =
          cabecalho.PerguntaMunicipioId ?? cabecalho.perguntaMunicipioId;
        const perguntaFaixaIdRaw =
          cabecalho.PerguntaFaixaId ?? cabecalho.perguntaFaixaId;

        const perguntaMunicipioId =
          perguntaMunicipioIdRaw !== undefined && perguntaMunicipioIdRaw !== null
            ? Number(perguntaMunicipioIdRaw)
            : null;
        const perguntaFaixaId =
          perguntaFaixaIdRaw !== undefined && perguntaFaixaIdRaw !== null
            ? Number(perguntaFaixaIdRaw)
            : null;

        const rawStatus = cabecalho.Status ?? cabecalho.status ?? "";
        const status = normalizeReviewStatus(rawStatus);

        this.revisao = {
          id:
            cabecalho.SeloRevisaoTecnicaId ??
            cabecalho.seloRevisaoTecnicaId ??
            cabecalho.SeloRecursoId ??
            cabecalho.seloRecursoId,
          municipioId: cabecalho.MunicipioId ?? cabecalho.municipioId,
          perguntaId: cabecalho.PerguntaId ?? cabecalho.perguntaId,
          perguntaTitulo: extractCriterionDescription(
            cabecalho.PerguntaTitulo ?? cabecalho.perguntaTitulo ?? ""
          ),
          grupoNome: cabecalho.GrupoNome ?? cabecalho.grupoNome,
          notaOriginal: cabecalho.NotaOriginal ?? cabecalho.notaOriginal,
          notaPleiteada: cabecalho.NotaPleiteada ?? cabecalho.notaPleiteada,
          notaHomologada: cabecalho.NotaHomologada ?? cabecalho.notaHomologada,
          status,
          rawStatus,
          dataCriacao: cabecalho.DataCriacao ?? cabecalho.dataCriacao,
          dataAtualizacao: cabecalho.DataAtualizacao ?? cabecalho.dataAtualizacao,
          responsavelNome:
            cabecalho.ResponsavelNome ?? cabecalho.responsavelNome,
          ehPerguntaClienteOculto: ehClienteOculto,
          perguntaMunicipioId,
          perguntaFaixaId,
        };

        this.faixaOptions = [];
        this.faixaLoadedForId = null;
        if (this.shouldShowFaixaSelect) {
          this.carregarFaixaOpcoes();
        } else {
          this.avaliacao.faixaRespostaId = null;
        }

        this.justificativa = data.Justificativa || data.justificativa || "";
        const anexos = data.Anexos || data.anexos || [];
        this.anexos = anexos.map((item) => ({
          id:
            item.SeloRevisaoTecnicaAnexoId ??
            item.seloRevisaoTecnicaAnexoId ??
            item.SeloRecursoAnexoId ??
            item.seloRecursoAnexoId,
          nome: item.Nome ?? item.nome,
          dataEnvio: item.DataEnvio ?? item.dataEnvio,
          usuarioNome: item.UsuarioNome ?? item.usuarioNome,
        }));

        const parecerFinal = data.ParecerFinal ?? data.parecerFinal ?? "";

        this.avaliacao.parecer = parecerFinal || "";

        const mensagens = data.Mensagens || data.mensagens || [];
        this.mensagens = mensagens
          .map((item) => {
            const rawStatus = item.Status ?? item.status ?? null;

            if (isReviewStatusGestaoNacional(rawStatus)) {
              return null;
            }

            const normalizedStatus = normalizeReviewStatus(rawStatus);

            return {
              id:
                item.SeloRevisaoTecnicaMensagemId ??
                item.seloRevisaoTecnicaMensagemId ??
                item.SeloRecursoMensagemId ??
                item.seloRecursoMensagemId,
              tipo: item.Tipo ?? item.tipo,
              status: normalizedStatus || null,
              rawStatus,
              mensagem: item.Mensagem ?? item.mensagem,
              notaHomologada: item.NotaHomologada ?? item.notaHomologada,
              dataCriacao: item.DataCriacao ?? item.dataCriacao,
              usuarioId: item.UsuarioId ?? item.usuarioId,
              usuarioNome: item.UsuarioNome ?? item.usuarioNome,
            };
          })
          .filter(Boolean)
          .sort((a, b) => new Date(a.dataCriacao) - new Date(b.dataCriacao));
      } catch (error) {
        console.error("Erro ao carregar revisão técnica", error);
        this.notifyError("Não foi possível carregar a revisão técnica");
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("pt-BR");
    },
    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    },
    formatScore(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      });
    },
    statusText(value) {
      const normalized = normalizeReviewStatus(value);
      return findStatusMeta(normalized || value).text;
    },
    timelineColor(mensagem) {
      if (mensagem.tipo === "STATUS") {
        const normalized = normalizeReviewStatus(
          mensagem.status || mensagem.rawStatus
        );
        return findStatusMeta(normalized || mensagem.status).color;
      }
      return "primary";
    },
    async carregarFaixaOpcoes() {
      if (!this.requiresFaixaSelection || !this.revisao) {
        this.faixaOptions = [];
        this.faixaLoadedForId = null;
        return;
      }

      const faixaId = this.revisao.perguntaFaixaId;
      if (!faixaId) {
        this.faixaOptions = [];
        this.faixaLoadedForId = null;
        return;
      }

      if (this.faixaLoading) {
        return;
      }

      if (this.faixaLoadedForId === faixaId && this.faixaOptions.length) {
        return;
      }

      this.faixaLoading = true;
      try {
        const { data } = await cityFaixaAPI.getByPerguntaFaixaId(faixaId);
        const lista = Array.isArray(data) ? data : [];
        this.faixaOptions = lista
          .map((item) => {
            const rawValue =
              item.PerguntaFaixaRespostaId ??
              item.perguntaFaixaRespostaId ??
              null;
            if (rawValue === null || rawValue === undefined) {
              return null;
            }

            const value = Number(rawValue);
            if (Number.isNaN(value)) {
              return null;
            }

            const nome = item.FaixaNome ?? item.faixaNome ?? "";
            const pontos = item.FaixaPontos ?? item.faixaPontos ?? 0;
            const pontosFormatados = this.formatScore(pontos);
            const texto = nome
              ? `${nome} (${pontosFormatados} pts)`
              : `${pontosFormatados} pts`;

            return {
              value,
              text: texto,
              pontos,
            };
          })
          .filter(Boolean);

        this.faixaLoadedForId = faixaId;
      } catch (error) {
        console.error("Erro ao carregar faixas de pontuação", error);
        this.notifyError(
          "Não foi possível carregar as faixas de pontuação disponíveis."
        );
        this.faixaOptions = [];
        this.faixaLoadedForId = null;
      } finally {
        this.faixaLoading = false;
      }
    },
    async download(anexo) {
      try {
        const { data } = await technicalReviewAPI.downloadAttachment(anexo.id);
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", anexo.nome || "anexo");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Erro ao baixar anexo", error);
        this.notifyError("Não foi possível baixar o anexo");
      }
    },
    async enviarComentario() {
      if (!this.novoComentario || this.actionLoading) return;
      this.actionLoading = true;
      try {
        await technicalReviewAPI.comment(this.revisao.id, {
          Mensagem: this.novoComentario,
        });
        this.novoComentario = "";
        await this.carregar();
      } catch (error) {
        console.error("Erro ao registrar comentário", error);
        this.notifyError("Não foi possível registrar o comentário");
      } finally {
        this.actionLoading = false;
      }
    },
    async enviarParaAnalise() {
      if (this.actionLoading) return;
      this.actionLoading = true;
      try {
        await technicalReviewAPI.send(this.revisao.id, {});
        this.notifySuccess("Revisão técnica enviada para análise");
        await this.carregar();
      } catch (error) {
        console.error("Erro ao enviar revisão técnica", error);
        this.notifyError("Não foi possível enviar a revisão técnica");
      } finally {
        this.actionLoading = false;
      }
    },
    async cancelarRevisaoTecnica() {
      if (!this.canCancel) {
        return;
      }
      if (!confirm("Confirma o cancelamento desta revisão técnica?")) {
        return;
      }
      if (this.actionLoading) {
        return;
      }
      this.actionLoading = true;
      try {
        await technicalReviewAPI.cancel(this.revisao.id, {});
        this.notifySuccess("Revisão técnica cancelada com sucesso");
        await this.carregar();
      } catch (error) {
        console.error("Erro ao cancelar revisão técnica", error);
        this.notifyError("Não foi possível cancelar a revisão técnica");
      } finally {
        this.actionLoading = false;
      }
    },
    async aplicarStatus() {
      if (!this.avaliacao.status) return;

      const precisaFaixa =
        this.avaliacao.status === "DEFERIDO" && this.requiresFaixaSelection;

      if (
        precisaFaixa &&
        (this.avaliacao.faixaRespostaId === null ||
          this.avaliacao.faixaRespostaId === undefined ||
          this.avaliacao.faixaRespostaId === "")
      ) {
        this.notifyError("Selecione a faixa de pontuação homologada");
        return;
      }

      const faixaRespostaId = precisaFaixa
        ? Number(this.avaliacao.faixaRespostaId)
        : null;

      if (precisaFaixa && Number.isNaN(faixaRespostaId)) {
        this.notifyError("Seleção de faixa de pontuação inválida");
        return;
      }

      const payload = {
        Status: this.avaliacao.status,
        Mensagem: this.avaliacao.mensagem,
        Parecer: this.avaliacao.parecer,
        NotaHomologada: null,
        PerguntaMunicipioId: this.revisao?.perguntaMunicipioId ?? null,
        PerguntaFaixaRespostaId: faixaRespostaId,
        arquivos: this.avaliacao.arquivos || [],
      };

      if (["DEFERIDO", "INDEFERIDO"].includes(payload.Status)) {
        const confirmouFinalizacao = confirm(
          "Ao finalizar a análise como Deferido ou Indeferido, a revisão técnica e sua análise não poderão mais ser editadas. Deseja continuar?"
        );

        if (!confirmouFinalizacao) {
          return;
        }
      }

      if (this.actionLoading) {
        return;
      }

      this.actionLoading = true;
      try {
        await technicalReviewAPI.changeStatus(this.revisao.id, payload);
        const finalizado = ["DEFERIDO", "INDEFERIDO"].includes(payload.Status);
        this.notifySuccess(
          finalizado
            ? "Análise finalizada com sucesso. A revisão técnica não poderá mais ser editada."
            : "Status da revisão técnica atualizado"
        );
        this.avaliacao = {
          status: null,
          mensagem: "",
          parecer: "",
          arquivos: [],
          faixaRespostaId: null,
        };
        await this.carregar();
      } catch (error) {
        console.error("Erro ao atualizar status", error);
        this.notifyError(
          "Não foi possível atualizar o status da revisão técnica"
        );
      } finally {
        this.actionLoading = false;
      }
    },
  },
};
</script>

<style scoped>
#technical-review-detail {
  min-height: 80vh;
}

.position-relative {
  position: relative;
}

.text-preline {
  white-space: pre-line;
}
</style>

